import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FaCheck, FaEnvelope, FaMinus, FaPaperPlane, FaSms, FaTimes } from 'react-icons/fa';
import {
  usePreviewRegistrationRequestsMutation,
  useSendRegistrationRequestsMutation
} from '../../mutations/communication.mutation';
import {
  ClientMissingRegistrations,
  RegistrationRequestPreviewResponse,
  RegistrationRequestSend,
  RegistrationRequestSendResponse
} from '../../types/communication.types';
import { NoticeChannelResult } from '../../types/registrationReview.types';

type ViewState = 'preview' | 'select' | 'results';

interface Props {
  show: boolean;
  onHide: () => void;
  clients: ClientMissingRegistrations[];
  onSent?: () => void;
}

const PREVIEW_HEIGHT = 460;

const canReach = (client: ClientMissingRegistrations) =>
  !!client.emailAddress || !!client.phoneNumber;

/** Email bodies reference the sample by cid, which only a mail client can resolve. */
const resolveSample = (html: string, response: RegistrationRequestPreviewResponse) =>
  html.split(response.sampleImageContentSrc).join(response.sampleImageDataUri);

function ChannelStatus({ result }: { result: NoticeChannelResult }) {
  if (result.status === 'Sent') {
    return (
      <span>
        <FaCheck color="#198754" className="me-1" />
        {result.recipient}
      </span>
    );
  }
  if (result.status === 'NoRecipient') {
    return (
      <span className="text-muted">
        <FaMinus className="me-1" />
        {result.error}
      </span>
    );
  }
  return (
    <span className="text-danger">
      <FaTimes className="me-1" />
      {result.recipient ? `${result.recipient}: ` : ''}
      {result.error}
    </span>
  );
}

function RegistrationRequestModal({ show, onHide, clients, onSent }: Props) {
  const [viewState, setViewState] = useState<ViewState>('preview');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [includeSecondaryEmails, setIncludeSecondaryEmails] = useState(false);
  const [preview, setPreview] = useState<RegistrationRequestPreviewResponse | null>(null);
  // Send exactly what was previewed, never the (possibly edited) selection.
  const [previewedRequest, setPreviewedRequest] = useState<RegistrationRequestSend | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [results, setResults] = useState<RegistrationRequestSendResponse | null>(null);

  const { mutate: buildPreview, isLoading: isPreviewing } =
    usePreviewRegistrationRequestsMutation();
  const { mutate: sendRequests, isLoading: isSending } = useSendRegistrationRequestsMutation();

  const loadPreview = useCallback(
    (clientIds: string[], includeSecondary: boolean) => {
      const request = { clientIds, includeSecondaryEmails: includeSecondary };
      setPreview(null);
      setPreviewedRequest(null);
      setPreviewIndex(0);
      setViewState('preview');
      buildPreview(request, {
        onSuccess: (data) => {
          setPreview(data ?? null);
          setPreviewedRequest(request);
        },
        onError: () => setViewState('select')
      });
    },
    [buildPreview]
  );

  // Opening goes straight to the preview for every client we can reach. Only the opening
  // resets state: the list refetches after a send and must not wipe the results.
  const wasShown = useRef(false);
  useEffect(() => {
    const opening = show && !wasShown.current;
    wasShown.current = show;
    if (!opening) return;

    const ids = clients.filter(canReach).map((c) => c.clientId);
    setSelectedClientIds(ids);
    setIncludeSecondaryEmails(false);
    setResults(null);

    if (ids.length > 0) loadPreview(ids, false);
    else setViewState('select');
  }, [show, clients, loadPreview]);

  const reachableClients = clients.filter(canReach);
  const allSelected =
    reachableClients.length > 0 && selectedClientIds.length === reachableClients.length;

  const toggleClient = (clientId: string) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedClientIds(allSelected ? [] : reachableClients.map((c) => c.clientId));
  };

  const handleSend = () => {
    if (!previewedRequest) return;
    sendRequests(previewedRequest, {
      onSuccess: (data) => {
        setResults(data ?? null);
        setViewState('results');
        if (onSent) onSent();
      }
    });
  };

  const previews = preview?.previews ?? [];
  const current = previews[previewIndex];

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {viewState === 'preview' && 'Preview Registration Requests'}
          {viewState === 'select' && 'Choose Recipients'}
          {viewState === 'results' && 'Results'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {viewState === 'preview' && (
          <>
            {isPreviewing || !preview ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <div className="small text-muted mt-2">Building preview...</div>
              </div>
            ) : !current ? (
              <Alert variant="warning">Nothing to preview.</Alert>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <strong>{current.clientName}</strong>
                    <div className="mt-1">
                      {current.missingPlateNumbers.map((plate) => (
                        <Badge bg="secondary" className="me-1" key={plate}>
                          {plate}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={previewIndex === 0}
                      onClick={() => setPreviewIndex((i) => i - 1)}>
                      Previous
                    </Button>
                    <span className="small text-nowrap">
                      {previewIndex + 1} of {previews.length}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={previewIndex >= previews.length - 1}
                      onClick={() => setPreviewIndex((i) => i + 1)}>
                      Next
                    </Button>
                  </div>
                </div>

                <Row className="g-3">
                  <Col lg={7}>
                    <h6 className="d-flex align-items-center gap-2">
                      <FaEnvelope /> Email
                    </h6>
                    {current.emailBody ? (
                      <>
                        <div className="small text-muted">To: {current.emailRecipient}</div>
                        <div className="small text-muted mb-2">Subject: {current.emailSubject}</div>
                        <iframe
                          title="Email preview"
                          sandbox=""
                          srcDoc={resolveSample(current.emailBody, preview)}
                          className="border rounded w-100"
                          style={{ height: PREVIEW_HEIGHT, backgroundColor: '#fff' }}
                        />
                      </>
                    ) : (
                      <Alert variant="secondary" className="small">
                        No email address on file. This client gets the text only.
                      </Alert>
                    )}
                  </Col>
                  <Col lg={5}>
                    <h6 className="d-flex align-items-center gap-2">
                      <FaSms /> Text message
                    </h6>
                    {current.smsMessage ? (
                      <>
                        <div className="small text-muted mb-2 d-flex align-items-center gap-2">
                          <span>To: {current.smsRecipient}</span>
                          {current.smsSendCount != null && current.smsSendCount > 1 && (
                            <Badge bg="warning" text="dark">
                              Request #{current.smsSendCount}
                            </Badge>
                          )}
                        </div>
                        <div
                          className="border rounded p-3"
                          style={{
                            height: PREVIEW_HEIGHT + 21,
                            overflowY: 'auto',
                            backgroundColor: '#f8f9fa'
                          }}>
                          <div style={{ maxWidth: '90%' }}>
                            <img
                              src={preview.sampleImageDataUri}
                              alt="Sample registration attached to the text"
                              className="rounded mb-1 d-block w-100"
                            />
                            <div
                              className="rounded-3 px-3 py-2 small"
                              style={{ backgroundColor: '#e9ecef', whiteSpace: 'pre-wrap' }}>
                              {current.smsMessage}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <Alert variant="secondary" className="small">
                        No phone number on file. This client gets the email only.
                      </Alert>
                    )}
                  </Col>
                </Row>
              </>
            )}
          </>
        )}

        {viewState === 'select' && (
          <>
            <p className="text-muted small">
              Emails and texts the selected customers asking for the registrations we do not have on
              file for their monitored plates. Each message lists only that customer&apos;s missing
              plates and includes a sample registration.
            </p>

            {clients.length > reachableClients.length && (
              <Alert variant="warning" className="py-2 small">
                {clients.length - reachableClients.length} client(s) have no email address or phone
                number on file and cannot be selected.
              </Alert>
            )}

            <Form.Check
              type="checkbox"
              id="include-secondary-emails"
              className="mb-3"
              label="Also send to alternate and secondary contact emails"
              checked={includeSecondaryEmails}
              onChange={(e) => setIncludeSecondaryEmails(e.target.checked)}
            />

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <Form.Check
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Missing Plates</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.clientId}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        disabled={!canReach(client)}
                        checked={selectedClientIds.includes(client.clientId)}
                        onChange={() => toggleClient(client.clientId)}
                        aria-label={`Select ${client.clientName}`}
                      />
                    </td>
                    <td>{client.clientName}</td>
                    <td className="small">
                      {client.emailAddress ?? <span className="text-muted fst-italic">None</span>}
                    </td>
                    <td className="small">
                      {client.phoneNumber ?? <span className="text-muted fst-italic">None</span>}
                    </td>
                    <td>
                      {client.missingPlateNumbers.map((plate) => (
                        <Badge bg="secondary" className="me-1" key={plate}>
                          {plate}
                        </Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {viewState === 'results' && results && (
          <>
            <Alert variant={results.emailsFailed + results.textsFailed > 0 ? 'warning' : 'success'}>
              Emails: <strong>{results.emailsSent}</strong> sent,{' '}
              <strong>{results.emailsFailed}</strong> failed. Texts:{' '}
              <strong>{results.textsSent}</strong> sent, <strong>{results.textsFailed}</strong>{' '}
              failed.
            </Alert>
            {results.sampleImageError && (
              <Alert variant="warning" className="small">
                The sample registration could not be attached to the texts, so they were sent
                without it: {results.sampleImageError}
              </Alert>
            )}
            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Text</th>
                </tr>
              </thead>
              <tbody>
                {results.results.map((result) => (
                  <tr key={result.clientId}>
                    <td>{result.clientName}</td>
                    <td className="small">
                      <ChannelStatus result={result.email} />
                    </td>
                    <td className="small">
                      <ChannelStatus result={result.sms} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {viewState === 'preview' && (
          <>
            <Button
              variant="outline-secondary"
              className="me-auto"
              disabled={isPreviewing || isSending}
              onClick={() => setViewState('select')}>
              Change Recipients
            </Button>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isPreviewing || isSending || !previewedRequest || previews.length === 0}
              onClick={handleSend}
              className="d-flex align-items-center gap-2">
              {isSending ? <Spinner animation="border" size="sm" /> : <FaPaperPlane />}
              Send to {previews.length} Client(s)
            </Button>
          </>
        )}

        {viewState === 'select' && (
          <>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={selectedClientIds.length === 0 || isPreviewing}
              onClick={() => loadPreview(selectedClientIds, includeSecondaryEmails)}
              className="d-flex align-items-center gap-2">
              <FaEnvelope />
              Preview {selectedClientIds.length} Client(s)
            </Button>
          </>
        )}

        {viewState === 'results' && (
          <Button variant="primary" onClick={onHide}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default RegistrationRequestModal;
